import React from "react";
import {OrbitComponent} from "../../base-components/orbit-component/orbit-component";
import {MaskCutoutBox, radialGradientModel} from "./boxes/mask-cutout.box";
import {MaskFabricBox} from "./boxes/mask-fabric/mask-fabric.box";
import {observer} from "mobx-react";
import {observable, runInAction, action, makeObservable} from "mobx";
import {PointDefinition, GeometryHelper, Circle, CircleDefinition} from "../../../services/onboarding-tour/geometry.helper";
import {computeSpotlightSVGPath, gradientTransformRotateProp} from "../../../services/onboarding-tour/onboarding-spotlight.helper";
import {SpotlightRaySvgBox} from "./boxes/spotlight-svg.box";
import {GradientStopSvgBox} from "./boxes/gradient-stop-svg/gradient-stop-svg.box";

export type ISpotlightTarget = CircleDefinition;

interface ISpotlightAnimationTarget extends ISpotlightTarget {
    opacity: number;
}

interface IOnboardingSpotlightComponentProps {
    zIndex: number;
    originPoint: PointDefinition;
    target: ISpotlightTarget | undefined;
    show: boolean;
}

@observer
export class OnboardingSpotlightComponent extends OrbitComponent<IOnboardingSpotlightComponentProps> {
    private maskElement: HTMLElement | null = null;
    private svgPathElement: SVGPathElement | null = null;
    private svgGradientElement: SVGLinearGradientElement | null = null;

    /**
     * Current target means where the spotlight is oriented in the present, and the target from props where it should be heading.
     * The animation takes place in between currentTarget and props.target and at the end, currentTarget get the value of props.target
     * If both are the same, no animation is currently running
     */
    @observable
    private currentTarget: ISpotlightTarget | undefined = undefined;
    @observable
    private scrollDisabled = false;

    // spotlightHome is for the first time the spotlight appears and for when it disappears
    // it defines the target from where it appears and where it disappear to
    // todo: use memoization
    private spotlightHome(P: PointDefinition): ISpotlightAnimationTarget {
        return {
            center: {
                x: P.x,
                y: P.y - 5
            },
            radius: 3,
            opacity: 0
        }
    };

    private animationPromise: Promise<void> = Promise.resolve();

    constructor(props: IOnboardingSpotlightComponentProps) {
        super(props);
        makeObservable(this);
    }

    @action
    setCurrentTarget(t: ISpotlightTarget | undefined) {
        if (!t) {
            this.currentTarget = undefined;
        } else {
            let tt: ISpotlightTarget = new Circle({x: t.center.x, y: t.center.y}, t.radius);
            this.currentTarget = tt;
        }
    }

    componentDidMount() {
        this.services.dom.disableScrolling();
        runInAction(() => {
            this.scrollDisabled = true;
        });
    }

    componentDidUpdate(prevProps: Readonly<IOnboardingSpotlightComponentProps>, prevState: Readonly<{}>, snapshot?: any): void {
        if (prevProps.target !== this.props.target) {
            this.scheduleAnimation(this.props.target);
        }
    }

    componentWillUnmount() {
        this.services.dom.enableScrolling();
    }

    captureMaskElement = (e: HTMLElement | null) => {
        this.maskElement = e;
    };
    captureSvgPathElement = (e: SVGPathElement | null) => {
        this.svgPathElement = e;
    };
    captureSvgGradientElement = (e: SVGLinearGradientElement | null) => {
        this.svgGradientElement = e;
    };

    render() {
        if (!this.scrollDisabled) {
            return null;
        }
        let currentTarget = !this.currentTarget ? this.spotlightHome(this.props.originPoint) : { ...this.currentTarget, opacity: 1};
        let zIndex = this.props.zIndex;

        return (<>
        <MaskCutoutBox ref={this.captureMaskElement} positionCoordinates={currentTarget.center} radius={currentTarget.radius} opacity={1 - currentTarget.opacity} zIndex={zIndex}>
            <MaskFabricBox show={this.props.show}></MaskFabricBox>
        </MaskCutoutBox>
        <SpotlightRaySvgBox viewBox={`0 0 ${window.innerWidth} ${window.innerHeight}`} zIndex={zIndex+1}>
            <defs>
                <linearGradient ref={this.captureSvgGradientElement} id="grad1" x1="0%" y1="0%" x2="100%" y2="0%" gradientTransform={gradientTransformRotateProp(this.props.originPoint, currentTarget.center)}>
                    <GradientStopSvgBox offset="0%" stopOpacity={0} />
                    <GradientStopSvgBox offset="100%" stopOpacity={1} />
                </linearGradient>
            </defs>
            <path
                ref={this.captureSvgPathElement} fill="url(#grad1)" fillRule="evenodd"
                style={{opacity: currentTarget.opacity}}
                d={computeSpotlightSVGPath(this.props.originPoint, currentTarget.center, currentTarget.radius)}
            />
        </SpotlightRaySvgBox>
        </>);
    }

    private scheduleAnimation = (t: ISpotlightTarget) => {
        this.animationPromise = this.animationPromise.then(() => {
            return this.animateToPoint(t);
        })
    }

    private animateToPoint(target: ISpotlightTarget | undefined) {
        if (!this.maskElement || !this.svgPathElement) {
            throw Error("No elements to animate");
        }
        let currentTarget = !this.currentTarget ? this.spotlightHome(this.props.originPoint) : { ...this.currentTarget, opacity: 1};
        let nextTarget = target ? {...target, opacity: 1} : this.spotlightHome(this.props.originPoint);
        let animationPromise = animateFromAToB((ratio) => {
            let intermediatePoint = GeometryHelper.getPointInSegment(currentTarget.center, nextTarget.center, ratio);
            let intermediateOpacity = GeometryHelper.getIntermediateCoordinate(currentTarget.opacity, nextTarget.opacity, ratio);
            let intermediateRadius = GeometryHelper.getIntermediateCoordinate(currentTarget.radius, nextTarget.radius, ratio);
            return {
                center: intermediatePoint,
                radius: intermediateRadius,
                opacity: intermediateOpacity
            };
        }, (intermediate) => {
            this.maskElement.style.webkitMaskImage = radialGradientModel(intermediate.radius, intermediate.center, 1 - intermediate.opacity);
            this.maskElement.style.maskImage = radialGradientModel(intermediate.radius, intermediate.center, 1- intermediate.opacity);

            this.svgGradientElement.setAttribute("gradientTransform", gradientTransformRotateProp(this.props.originPoint, intermediate.center));

            this.svgPathElement.setAttribute("d", computeSpotlightSVGPath(this.props.originPoint, intermediate.center, intermediate.radius));
            this.svgPathElement.style.opacity = intermediate.opacity.toString();
        }).catch(() => {
            // the animation is a "best-effort". if it fails due to failing of intermediate point computations we ignore it ...
            return;
        }).finally(() => {
            // and set the target. That will rerender the component with the target
            this.setCurrentTarget(target);
        });
        return animationPromise;
    }

}

/**
 *
 * @param intermediateDataComputer a function given a ratio (number between 0 and 1) that will modify the parameters that need to be animated
 * @param duration number. A number representing miliseconds to know how much time has passed since the last repaint of the screen
 */
function animateFromAToB(intermediateDataComputer: (ratio: number) => ISpotlightAnimationTarget, effectsCallback: (p: ISpotlightAnimationTarget) => void, duration: number = 300) {
    return new Promise<void>((resolve, reject) => {
        let animationDone = false;
        let startTimestamp: number | undefined;
        let previousTimestamp: number| undefined;
        let tween = (timestamp: number) => {
            if (startTimestamp === undefined) {
                startTimestamp = timestamp;
            }
            const elapsed = timestamp - startTimestamp;
            if (previousTimestamp !== timestamp) {
                const ratio = Math.min(elapsed / duration, 1);

                try {
                    let intermediatePoint = intermediateDataComputer(ratio);
                    effectsCallback(intermediatePoint);
                } catch (e) {
                    startTimestamp = undefined;
                    previousTimestamp = undefined;
                    reject();
                }

                if (ratio === 1) {
                    animationDone = true;
                }
            }
            previousTimestamp = timestamp;
            if (!animationDone) {
                window.requestAnimationFrame(tween);
            } else {
                startTimestamp = undefined;
                previousTimestamp = undefined;
                resolve();
            }
        }

        window.requestAnimationFrame(tween);
    });
}